import { useCallback, useEffect, useRef, useState } from 'react';

// Reads the device's orientation (accelerometer) to tell whether the phone is
// being held UPRIGHT — vertical/perpendicular to the floor, back camera facing
// a vertical screen straight-on. That's the ideal for a parallel shot.
//
// In DeviceOrientation terms the ideal is beta ~= 90 (phone standing straight
// up) and gamma ~= 0 (no left/right roll). We expose the *deviation* from that
// ideal (pitch/roll) so the UI can centre a bubble when the phone is upright.
//
// iOS 13+ gates DeviceOrientationEvent behind an explicit permission prompt
// that must be triggered from a user gesture; other platforms expose it freely.

export type TiltPermission = 'unknown' | 'granted' | 'denied' | 'unsupported';

export const LEVEL_THRESHOLD_DEG = 6; // within this on both axes => "upright"
export const IDEAL_BETA_DEG = 90;     // phone held vertical/upright
const THROTTLE_MS = 100;

// iOS exposes a static requestPermission() on the event constructor.
type DeviceOrientationEventiOS = typeof DeviceOrientationEvent & {
    requestPermission?: () => Promise<'granted' | 'denied'>;
};

export function useDeviceTilt(thresholdDeg: number = LEVEL_THRESHOLD_DEG) {
    const supported = typeof window !== 'undefined' && 'DeviceOrientationEvent' in window;
    const needsPermission =
        supported &&
        typeof (window.DeviceOrientationEvent as DeviceOrientationEventiOS).requestPermission === 'function';

    const [permission, setPermission] = useState<TiltPermission>(
        supported ? (needsPermission ? 'unknown' : 'granted') : 'unsupported'
    );
    const [beta, setBeta] = useState(0);   // front-back tilt
    const [gamma, setGamma] = useState(0); // left-right tilt
    const lastUpdate = useRef(0);

    const handleOrientation = useCallback((e: DeviceOrientationEvent) => {
        const now = performance.now();
        if (now - lastUpdate.current < THROTTLE_MS) return;
        lastUpdate.current = now;
        let b = e.beta ?? 0;
        if (b > 180) b -= 360; // normalise into -180..180
        setBeta(b);
        setGamma(e.gamma ?? 0);
    }, []);

    useEffect(() => {
        if (!supported || permission !== 'granted') return;
        window.addEventListener('deviceorientation', handleOrientation, true);
        return () => window.removeEventListener('deviceorientation', handleOrientation, true);
    }, [supported, permission, handleOrientation]);

    const requestPermission = useCallback(async () => {
        if (!needsPermission) {
            setPermission('granted');
            return;
        }
        try {
            const ctor = window.DeviceOrientationEvent as DeviceOrientationEventiOS;
            const res = await ctor.requestPermission!();
            setPermission(res === 'granted' ? 'granted' : 'denied');
        } catch {
            setPermission('denied');
        }
    }, [needsPermission]);

    // Deviation from the ideal upright pose. Only pitch (front-back tilt)
    // matters for a square-on shot of a vertical screen; sideways roll is
    // ignored. pitch = 0 when the phone is vertical (beta == 90).
    const pitch = beta - IDEAL_BETA_DEG;
    const roll = gamma;
    const isLevel = Math.abs(pitch) <= thresholdDeg;

    return { supported, needsPermission, permission, requestPermission, beta, gamma, pitch, roll, isLevel };
}
